// components/quotes/components/quoteTable.jsx
import React, { useState } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, DropdownMenuSeparator, } from '../../../../components/shareable/dropdownMenu';
import { MoreHorizontal, Eye, ChevronUp, ChevronDown, AlertCircle, Edit, CheckCircle2, Trash2, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../../components/shareable/confirmModal';

const QuoteTable = ({ quotes = [], currentPage, indexOfFirstItem, onViewPdf, onSendQuote, sortConfig, onSort, allQuotes = [], isLatestVersion, onDeleteQuote }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleUpdate = (quote) => { navigate(`/update-quote/${quote._id}`); };

  // Render the quote's stored creation date. The DB document keeps it on the `date`
  // field (set by the backend on create); `created_at` is only a legacy fallback.
  const formatCreatedAt = (quote) => {
    const value = quote?.date || quote?.created_at;
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewPdf = (quoteNumber, e) => {
    e.stopPropagation();
    onViewPdf(quoteNumber);
  };

  const handleSendQuote = (quote, e) => {
    e.stopPropagation();
    if (onSendQuote) onSendQuote(quote);
  };

  const handleDeleteClick = (quote, e) => {
    e.stopPropagation();
    setQuoteToDelete(quote);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (quoteToDelete) {
      setIsDeleting(true);
      const success = await onDeleteQuote(quoteToDelete.quote_number);
      setIsDeleting(false);
      
      if (success) {
        setShowDeleteConfirm(false);
        setQuoteToDelete(null);
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setQuoteToDelete(null);
  };

  // Helper function to render sort arrows
  const renderSortArrow = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <ChevronUp className="h-4 w-4 text-gray-400 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-4 w-4 text-blue-600" />
      : <ChevronDown className="h-4 w-4 text-blue-600" />;
  };

  // Helper function to handle column header click
  const handleColumnClick = (columnKey, e) => {
    e.stopPropagation();
    if (onSort) {
      onSort(columnKey);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Index
              </th>
              <th 
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => handleColumnClick('quote_number', e)}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Quote Number</span>
                  {renderSortArrow('quote_number')}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => handleColumnClick('customer', e)}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Customer Name</span>
                  {renderSortArrow('customer')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => handleColumnClick('customer_rfq_number', e)}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Customer RFQ</span>
                  {renderSortArrow('customer_rfq_number')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => handleColumnClick('quote_total', e)}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Grand Total</span>
                  {renderSortArrow('quote_total')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={(e) => handleColumnClick('date', e)}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Date Created</span>
                  {renderSortArrow('date')}
                </div>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {quotes && quotes.length > 0 ? (
              quotes.map((quote, index) => {
                const isLatest = isLatestVersion ? isLatestVersion(quote, allQuotes) : false;
                // Calculate sequential index: start from indexOfFirstItem + 1, then add the current index
                const displayIndex = indexOfFirstItem + index + 1;
                // Create a unique key combining _id, quote_number, and index to prevent React key conflicts
                const uniqueKey = quote._id 
                  ? `${quote._id}-${currentPage}-${index}` 
                  : `${quote.quote_number || 'quote'}-${currentPage}-${index}`;
                
                return (
                  <tr 
                    key={uniqueKey} 
                    className={`transition-colors cursor-pointer ${isLatest ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'}`}
                    onClick={() => onViewPdf(quote.quote_number)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {displayIndex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {quote.quote_number || <span className="italic text-gray-400">N/A</span>}
                        {isLatest && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                            <CheckCircle2 className="h-3 w-3" />
                            Latest
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-[200px] break-words text-center">
                      {quote.customer || <span className="italic text-gray-400">N/A</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-[200px] break-words text-center">
                      {quote.customer_rfq_number || <span className="italic text-gray-400">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {Number.isFinite(quote.grand_total) && quote.grand_total > 0
                        ? `$${quote.grand_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : <span className="italic text-gray-400">N/A</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {formatCreatedAt(quote) || <span className="italic text-gray-400">N/A</span>}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger className="focus:outline-none">
                          <MoreHorizontal className="h-5 w-5 text-gray-500 hover:text-blue-600 transition-colors mx-auto" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-white shadow-xl rounded-lg border border-gray-200 min-w-[200px] py-2 z-50 -left-12">
                          <DropdownMenuLabel className="px-4 py-2 text-xs font-semibold text-gray-700 text-center border-b border-gray-100">
                            Actions
                          </DropdownMenuLabel>
                          <DropdownMenuItem className="px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors flex items-center rounded-md mx-1 my-1" onClick={(e) => handleViewPdf(quote.quote_number, e)} >
                            <Eye className="h-4 w-4 mr-3 text-red-500" />
                            View PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem className="px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors flex items-center rounded-md mx-1 my-1" onClick={(e) => handleSendQuote(quote, e)} >
                            <Mail className="h-4 w-4 mr-3 text-blue-500" />
                            Send Quote
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="my-1" />
                          {isLatest && (
                            <DropdownMenuItem 
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer transition-colors flex items-center rounded-md mx-1 my-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdate(quote);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-3 text-green-500" />
                              Update Quote
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors flex items-center rounded-md mx-1 my-1"
                            onClick={(e) => handleDeleteClick(quote, e)}
                          >
                            <Trash2 className="h-4 w-4 mr-3 text-red-500" />
                            Delete Quote
                          </DropdownMenuItem>
                          {!isLatest && (
                            <div className="px-4 py-2.5 mx-1 my-1 bg-gray-50 rounded-md">
                              <div className="flex items-center text-sm text-gray-500">
                                <AlertCircle className="h-4 w-4 mr-2 text-amber-500" />
                                <span className="font-medium">Not latest version - cannot edit</span>
                              </div>
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500" >
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    No quotes available
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal isOpen={showDeleteConfirm} onClose={cancelDelete} onConfirm={confirmDelete} title="Delete Quote" description={`Are you sure you want to delete quote ${quoteToDelete?.quote_number}? This action cannot be undone.`} isLoading={isDeleting} confirmBtnText="Cancel" cancelBtnText="Delete" />
    </>
  );
};

export default QuoteTable;
